import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import type {
  ReactionParameterUnitTypeQuery,
  ReactionParameterUnitTypeQueryVariables,
} from 'notebook/integrations/chemistry/__generated/ReactionParameterUnitTypeQuery';
import {REACTION_PARAMETER_UNIT_TYPE_QUERY} from 'notebook/integrations/chemistry/reactionParameterQueries';
import {isCellLoading} from 'notebook/tables/excess/Util';
import {
  RowScopedUnitTypeFilterArgs,
  RowScopedUnitTypeFilterResult,
} from 'notebook/tables/search-scoping/rowScopedUnitTypeFilter';
import {getLibraryDefinitionTypedValue} from 'procedures/parameter-library/libraryParameterDefinitionTypedValueUtils';
import {assertNever} from 'util/Types';

/**
 * Restricts the Reaction Conditions Unit picker to the unit type of the row's library parameter. Only FLOAT
 * parameters with a unit type restrict the picker; every other parameter leaves it unrestricted.
 *
 * The picker must not offer units before the restriction is known, so it reports loading until the parameter
 * definition is fetched.
 */
export default function useReactionParameterUnitTypeFilter({
  sourceCell: parameterCell,
}: RowScopedUnitTypeFilterArgs): RowScopedUnitTypeFilterResult {
  const libraryDefinitionId = getLibraryDefinitionTypedValue(parameterCell?.value)?.id;
  const queryResult = useSkippableBenchlingQuery<
    ReactionParameterUnitTypeQuery,
    ReactionParameterUnitTypeQueryVariables
  >(REACTION_PARAMETER_UNIT_TYPE_QUERY, {
    variables: {id: libraryDefinitionId ?? ''},
    skip: libraryDefinitionId == null,
  });
  // A Parameter entered as text is still a string until it resolves to a library definition, so there's no id to
  // query yet.
  if (parameterCell != null && isCellLoading(parameterCell)) {
    return {loading: true};
  }
  switch (queryResult.type) {
    case QueryResultType.PENDING:
      return {loading: true};
    case QueryResultType.SKIPPED:
    case QueryResultType.ERROR:
      return {loading: false, predicate: null};
    case QueryResultType.SUCCESS: {
      const definition = queryResult.data.libraryParameterDefinition;
      const unitTypeId = definition.dataType === FieldDefinitionType.FLOAT ? definition.unitType?.id : null;
      return {
        loading: false,
        predicate: unitTypeId != null ? (unitType) => unitType.id === unitTypeId : null,
      };
    }
    default:
      return assertNever(queryResult);
  }
}
